import { type BoxRect, type GeometryResolver } from '../types';

export class DomTableGeometryResolver implements GeometryResolver {
  #cachedWidths: { dragHandle: number; actions: number; total: number } | null = null;
  #getContainer: () => HTMLElement | null;

  constructor(getContainer: () => HTMLElement | null) {
    this.#getContainer = getContainer;
  }

  getScrollContainer(): HTMLElement | null {
    return this.#getContainer();
  }

  getCellBounds(rowIndex: number, colIndex: number): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    const cell = container.querySelector(
      `[data-row-index="${rowIndex}"][data-col-index="${colIndex}"]`
    ) as HTMLElement | null;

    if (!cell) {
      return null;
    }

    return this.#getRelativeRect(cell, container);
  }

  getRowBounds(rowIndex: number): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    // Use ANY valid cell in that row to get measurements
    const cell = container.querySelector(
      `[data-row-index="${rowIndex}"]`
    ) as HTMLElement | null;

    if (!cell) {
      return null;
    }

    const cellRect = this.#getRelativeRect(cell, container);
    const ignored = this.#getIgnoredCellWidths(container);

    return {
      x: ignored.dragHandle,
      y: cellRect.y,
      width: container.scrollWidth - ignored.total,
      height: cellRect.height,
    };
  }

  getColumnBounds(colIndex: number): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    // Use ANY valid cell in that column to get measurements
    const cell = container.querySelector(
      `[data-col-index="${colIndex}"]`
    ) as HTMLElement | null;

    if (!cell) {
      return null;
    }

    const cellRect = this.#getRelativeRect(cell, container);

    return {
      x: cellRect.x,
      y: 0,
      width: cellRect.width,
      height: container.scrollHeight,
    };
  }

  getContainerBounds(): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    const width = Math.max(container.scrollWidth, container.clientWidth);
    const height = Math.max(container.scrollHeight, container.clientHeight);

    if (width === 0 || height === 0) {
      return null;
    }

    const ignored = this.#getIgnoredCellWidths(container);

    return {
      x: ignored.dragHandle,
      y: 0,
      width: width - ignored.total,
      height,
    };
  }

  // ============================================================================
  // PRIVATE DOM MATH HELPERS
  // ============================================================================

  #getRelativeRect(el: HTMLElement, container: HTMLElement): BoxRect {
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return {
      x: elRect.left - containerRect.left + container.scrollLeft,
      y: elRect.top - containerRect.top + container.scrollTop,
      width: elRect.width,
      height: elRect.height,
    };
  }

  #getIgnoredCellWidths(container: HTMLElement) {
    if (this.#cachedWidths) {
      return this.#cachedWidths;
    }

    const firstRowLeftOffsetCell = container.querySelector(
      'tr:first-child td.ignored-td-class:first-child'
    ) as HTMLTableCellElement | null;

    const firstRowRightOffsetCell = container.querySelector(
      'tr:first-child td.ignored-td-class:last-child'
    ) as HTMLTableCellElement | null;

    const dragHandle = firstRowLeftOffsetCell?.offsetWidth ?? 0;
    const actions = firstRowRightOffsetCell?.offsetWidth ?? 0;

    this.#cachedWidths = {
      dragHandle,
      actions,
      total: dragHandle + actions,
    };

    return this.#cachedWidths;
  }
}
